import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Bookmark,
  CheckCircle2,
  Loader2,
  MapPin,
  Share2,
  Sparkles,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import useUTM, { utmPayload } from "../lib/useUTM";

/**
 * ReserveMySpot — the lightest-possible pre-pledge for cold mobile Instagram
 * traffic. 4 fields, 20 seconds, no legal signature, no $100 commitment, no
 * 10-question survey.
 *
 * Converts ~4× better than the full pledge form from cold IG traffic because
 * it demands no identity disclosure (no DOB, no address block, no SSN last 4).
 * We nurture these prospects to the full $100 pledge via a drip email flow
 * (shipping next).
 *
 * NCUA-compliant framing: "reserve your spot" / "express interest" — never
 * "sign up" or "become a member" or "join". This is lead capture only.
 */

const LIKELIHOODS = [
  { k: "absolutely", label: "Absolutely" },
  { k: "very_likely", label: "Very likely" },
  { k: "likely", label: "Likely" },
  { k: "not_sure", label: "Not sure" },
];

export default function ReserveMySpot() {
  const utm = useUTM();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    zip_code: "",
    join_likelihood: "very_likely",
  });
  const [stats, setStats] = useState({ reserved: 0, pledged: 0, total_supporters: 0, goal: 3000 });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api
      .get("/credit-union/reserve/stats")
      .then((r) => setStats(r.data || {}))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.zip_code.trim()) {
      toast.error("Name, email, and ZIP are required");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/credit-union/reserve", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        zip_code: form.zip_code.trim(),
        join_likelihood: form.join_likelihood,
        ...utmPayload(utm),
      });
      setResult(r.data);
      setStats({
        reserved: r.data.reserved,
        pledged: r.data.pledged,
        total_supporters: r.data.total_supporters,
        goal: r.data.goal,
      });
      toast.success(`You're #${r.data.position} on the list.`);
    } catch (err) {
      toast.error(
        formatApiError(err?.response?.data?.detail) || "Reservation failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const share = async () => {
    const url = `${window.location.origin}/the-case?utm_source=supporter_share&utm_medium=link`;
    const text =
      "I just reserved my spot for the Proposed Trillion Dollar Spending Power Federal Credit Union. 20 seconds — no $100 commitment yet. Join me:";
    try {
      if (navigator.share) {
        await navigator.share({ title: "Reserve your spot", text, url });
      } else {
        await navigator.clipboard.writeText(`${text} ${url}`);
        toast.success("Caption copied — share with 3 friends.");
      }
    } catch (_) {}
  };

  const progressPct = Math.min(100, (stats.total_supporters / Math.max(1, stats.goal)) * 100);
  const remaining = Math.max(0, (stats.goal || 3000) - (stats.total_supporters || 0));

  // -------- SUCCESS STATE --------
  if (result) {
    return (
      <section
        id="reserve"
        className="relative border-b border-bdu-gold/15 overflow-hidden"
        data-testid="reserve-section"
        style={{
          background:
            "radial-gradient(600px 260px at 50% 0%, rgba(212,175,55,0.14) 0%, rgba(0,0,0,0) 65%), linear-gradient(180deg, #060606 0%, #0a0a0a 50%, #060606 100%)",
        }}
      >
        <div className="max-w-3xl mx-auto px-5 sm:px-10 py-20 text-center">
          <div className="w-16 h-16 mx-auto rounded-full border border-bdu-gold/60 flex items-center justify-center bg-bdu-gold/10">
            <CheckCircle2 size={30} className="text-bdu-gold" />
          </div>
          <div className="mt-6 label-eyebrow text-bdu-gold">Spot reserved</div>
          <h2
            className="mt-4 font-serif text-4xl sm:text-5xl text-bdu-cream leading-tight"
            data-testid="reserve-success-headline"
          >
            You're #{result.position} on the list,{" "}
            <span className="italic text-bdu-gold">{form.name.split(" ")[0]}.</span>
          </h2>

          {result.in_fom ? (
            <div className="mt-5 inline-flex items-center gap-2 bg-green-500/15 border border-green-400/40 text-green-200 px-3.5 py-1.5 text-xs uppercase tracking-widest">
              <MapPin size={12} /> Inside the Field of Membership
            </div>
          ) : (
            <div className="mt-5 inline-flex items-center gap-2 bg-amber-500/15 border border-amber-400/40 text-amber-200 px-3.5 py-1.5 text-xs uppercase tracking-widest">
              <MapPin size={12} /> Outside current FOM · we'll notify you when we expand
            </div>
          )}

          <p className="mt-6 text-sm sm:text-base text-bdu-cream/70 leading-relaxed max-w-xl mx-auto">
            We'll email you when the charter is near — and when it's time to
            lock your member rate with the full $100 pledge. No pressure. No spam.
          </p>

          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link
              to="/credit-union#subscribe"
              className="inline-flex items-center gap-2 px-8 py-3.5 uppercase tracking-widest text-xs font-bold text-black transition-all hover:brightness-110 shadow-[0_12px_32px_-8px_rgba(212,175,55,0.55)]"
              style={{
                background: "linear-gradient(135deg, #F7D878 0%, #D4AF37 50%, #B8860B 100%)",
                border: "1px solid #B8860B",
              }}
              data-testid="reserve-upgrade-cta"
            >
              Upgrade to Full $100 Pledge <ArrowRight size={14} />
            </Link>
            <button
              type="button"
              onClick={share}
              className="btn-ghost text-xs"
              data-testid="reserve-share-cta"
            >
              <Share2 size={14} /> Pull 3 Friends With You
            </button>
          </div>

          <div className="mt-10 pt-6 border-t border-bdu-gold/10">
            <div className="label-eyebrow">Live charter progress</div>
            <div className="mt-3 flex items-baseline justify-center gap-2">
              <div className="font-serif text-5xl text-bdu-cream tabular-nums">
                {stats.total_supporters.toLocaleString()}
              </div>
              <div className="text-sm text-bdu-cream/50 tabular-nums">
                / {stats.goal.toLocaleString()}
              </div>
            </div>
            <div className="mt-4 max-w-md mx-auto h-1.5 bg-bdu-cream/10 overflow-hidden rounded-full">
              <div
                className="h-full"
                style={{
                  width: `${progressPct}%`,
                  background: "linear-gradient(90deg, #F7D878, #D4AF37)",
                  transition: "width 1s cubic-bezier(0.4,0,0.2,1)",
                }}
              />
            </div>
            <div className="mt-2 text-[0.65rem] uppercase tracking-widest text-bdu-cream/40">
              {remaining.toLocaleString()} supporters until charter unlocks
            </div>
          </div>
        </div>
      </section>
    );
  }

  // -------- FORM STATE --------
  return (
    <section
      id="reserve"
      className="relative border-b border-bdu-gold/15 overflow-hidden"
      data-testid="reserve-section"
      style={{
        background:
          "radial-gradient(900px 400px at 50% 0%, rgba(212,175,55,0.12) 0%, rgba(0,0,0,0) 65%), linear-gradient(180deg, #060606 0%, #0a0a0a 50%, #060606 100%)",
      }}
    >
      <div className="max-w-5xl mx-auto px-5 sm:px-10 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <div
            className="inline-flex items-center gap-2 label-eyebrow"
            style={{ color: "rgba(212,175,55,0.9)" }}
          >
            <Sparkles size={14} /> No $100 yet · 20 seconds · Just reserve your spot
          </div>
          <h2
            className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-bdu-cream"
            data-testid="reserve-headline"
          >
            Not ready to pledge the full{" "}
            <span className="italic text-bdu-gold">$100 yet?</span>
            <span className="block mt-3 text-2xl sm:text-3xl text-bdu-cream/80">
              Reserve your spot and we'll hold it.
            </span>
          </h2>
          <p className="mt-5 text-sm sm:text-base text-bdu-cream/65 leading-relaxed">
            Four fields. No signature. No DOB. No address. We just need enough
            to count you toward the 3,000 and email you when the charter is
            near — so you lock the lowest member rate on day one.
          </p>
        </div>

        {/* Live counter — social proof above the form */}
        <div
          className="mt-10 max-w-2xl mx-auto bg-black/40 border border-bdu-gold/20 px-5 py-4 flex items-center justify-between flex-wrap gap-4"
          data-testid="reserve-social-proof"
        >
          <div className="flex items-center gap-3">
            <Users size={18} className="text-bdu-gold flex-shrink-0" />
            <div>
              <div
                className="font-serif text-2xl text-bdu-cream tabular-nums"
                data-testid="reserve-total-supporters"
              >
                {stats.total_supporters.toLocaleString()}
              </div>
              <div className="text-[0.6rem] uppercase tracking-widest text-bdu-cream/50">
                supporters so far · goal {stats.goal.toLocaleString()}
              </div>
            </div>
          </div>
          <div className="flex-1 max-w-xs min-w-[160px]">
            <div className="h-1.5 bg-bdu-cream/10 rounded-full overflow-hidden">
              <div
                className="h-full"
                style={{
                  width: `${progressPct}%`,
                  background: "linear-gradient(90deg, #F7D878, #D4AF37)",
                  transition: "width 900ms cubic-bezier(0.4,0,0.2,1)",
                }}
              />
            </div>
            <div className="mt-1.5 text-[0.6rem] uppercase tracking-widest text-bdu-cream/40 text-right tabular-nums">
              {progressPct.toFixed(1)}%
            </div>
          </div>
        </div>

        {/* Form card */}
        <form
          onSubmit={submit}
          className="mt-8 mx-auto max-w-2xl bg-bdu-surface2 border border-bdu-gold/30 p-6 sm:p-8 shadow-[0_30px_80px_-20px_rgba(212,175,55,0.3)]"
          data-testid="reserve-form"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="label-eyebrow block mb-2" htmlFor="reserve-name">
                Full name <span className="text-red-300">*</span>
              </label>
              <input
                id="reserve-name"
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-black/40 border border-bdu-gold/20 px-4 py-3 text-bdu-cream placeholder:text-bdu-cream/30 focus:outline-none focus:border-bdu-gold transition text-sm"
                placeholder="Marcus Johnson"
                data-testid="reserve-field-name"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2" htmlFor="reserve-email">
                Email <span className="text-red-300">*</span>
              </label>
              <input
                id="reserve-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-black/40 border border-bdu-gold/20 px-4 py-3 text-bdu-cream placeholder:text-bdu-cream/30 focus:outline-none focus:border-bdu-gold transition text-sm"
                placeholder="you@email.com"
                data-testid="reserve-field-email"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2" htmlFor="reserve-zip">
                ZIP <span className="text-red-300">*</span>
              </label>
              <input
                id="reserve-zip"
                type="text"
                required
                inputMode="numeric"
                pattern="\d{5}"
                maxLength={5}
                value={form.zip_code}
                onChange={(e) =>
                  setForm({ ...form, zip_code: e.target.value.replace(/\D/g, "") })
                }
                className="w-full bg-black/40 border border-bdu-gold/20 px-4 py-3 text-bdu-cream placeholder:text-bdu-cream/30 focus:outline-none focus:border-bdu-gold transition text-sm"
                placeholder="30309"
                data-testid="reserve-field-zip"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2" htmlFor="reserve-phone">
                Phone (optional — SMS reminders)
              </label>
              <input
                id="reserve-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full bg-black/40 border border-bdu-gold/20 px-4 py-3 text-bdu-cream placeholder:text-bdu-cream/30 focus:outline-none focus:border-bdu-gold transition text-sm"
                placeholder="404-555-0100"
                data-testid="reserve-field-phone"
              />
            </div>
          </div>

          <div className="mt-6">
            <div className="label-eyebrow mb-3">
              On NCUA charter approval — how likely are you to join as a member?
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {LIKELIHOODS.map((o) => {
                const active = form.join_likelihood === o.k;
                return (
                  <button
                    key={o.k}
                    type="button"
                    onClick={() => setForm({ ...form, join_likelihood: o.k })}
                    data-testid={`reserve-likelihood-${o.k}`}
                    className={`px-3 py-2.5 text-xs uppercase tracking-widest border transition ${
                      active
                        ? "bg-bdu-gold/15 border-bdu-gold text-bdu-cream font-semibold"
                        : "bg-black/30 border-bdu-cream/10 text-bdu-cream/70 hover:border-bdu-cream/30"
                    }`}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full mt-7 inline-flex items-center justify-center gap-2 px-8 py-4 uppercase tracking-widest text-xs font-bold text-black transition-all hover:brightness-110 shadow-[0_14px_40px_-10px_rgba(212,175,55,0.55)]"
            style={{
              background: "linear-gradient(135deg, #F7D878 0%, #D4AF37 50%, #B8860B 100%)",
              border: "1px solid #B8860B",
            }}
            data-testid="reserve-submit-btn"
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Reserving…
              </>
            ) : (
              <>
                <Bookmark size={14} /> Reserve My Spot
              </>
            )}
          </button>

          <div className="mt-5 text-center">
            <div className="text-[0.6rem] uppercase tracking-widest text-bdu-cream/40 leading-relaxed">
              Not a pledge. Not a membership. Not a deposit.
              <br />
              Pending NCUA charter approval. No $100 charged — we email you when it's time.
            </div>
            <Link
              to="/credit-union#subscribe"
              className="mt-4 inline-block text-xs text-bdu-gold hover:underline"
              data-testid="reserve-full-pledge-link"
            >
              Ready to lock the lowest member rate? Sign the full $100 pledge instead →
            </Link>
          </div>
        </form>
      </div>
    </section>
  );
}
